import { UserOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Avatar,
  Button,
  Flex,
  Form,
  FormProps,
  Image,
  Input,
  Popconfirm,
  Skeleton,
  Space,
  Tabs,
  TabsProps,
  Tag,
  Typography,
} from "antd";
import { createStyles } from "antd-style";
import { BooleanReturnType, useBoolean } from "hooks/use-boolean";
import { get, isEmpty } from "lodash";
import moment from "moment";
import { memo, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "services/axios";
import { TbDoorEnter, TbDoorExit } from "react-icons/tb";
import { lateTypes } from "utils/constants";
import { MdDelete } from "react-icons/md";
import { FaExpandAlt } from "react-icons/fa";

const useStyle = createStyles(({ css, token }) => {
  return {
    dropDownBody: css`
      background: white;
      height: 300px;
      width: 450px;
      overflow-y: auto;
      border-radius: ${token.borderRadiusLG}px;
      padding: 10px;
      box-shadow: ${token.boxShadow};

      .info-header {
        margin-bottom: 10px;
      }

      .history-list {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        .history-item {
          margin-bottom: 5px;
          padding: 5px;
          width: 100%;
          box-sizing: border-box;
        }
      }
    `,
  };
});

const permissionTypes: any = {
  PERSONAL_WORK: "Personal work",
  OFFICE_WORK: "Office work",
};

interface Props {
  data: any;
  usersMap: any;
  refetchQuery: VoidFunction;
  isAdmin: boolean;
}

const LateInfoDropdownBody = (props: Props) => {
  const { styles } = useStyle();
  const [activeKey, setActiveKey] = useState("0");
  const items: TabsProps["items"] = useMemo(() => {
    const items = [];

    if (get(props, "data.lateInfoList.length")) {
      items.push({
        key: "0",
        label: "Latencies data",
        children: <UserLateInfoTab {...props} />,
      });
    }

    items.push({
      key: items.length + "",
      label: "In out data",
      children: <UserInOutInfoTab {...props} />,
    });

    if (get(props, "data.temporaryBreaks.length")) {
      items.push({
        key: items.length + 1 + "",
        label: "Leaving permissions",
        children: <LeaveRequestsTab {...props} />,
      });
    }

    return items;
  }, [props]);

  useEffect(() => {
    if (!isEmpty(props.data)) {
      setActiveKey("0");
    }
  }, [props]);

  return (
    <div className={styles.dropDownBody + " custom-scrollbar"}>
      <Tabs
        activeKey={activeKey}
        onChange={setActiveKey}
        size="small"
        items={items}
      />
    </div>
  );
};

const UserLateInfoTab = ({ data, usersMap, refetchQuery }: Props) => {
  const [lateInfoList, setLateInfoList] = useState([]);
  const changingBool = useBoolean();

  const onChangeFinished = (data: any) => {
    setLateInfoList(data);
    refetchQuery();
    changingBool.onFalse();
  };

  useEffect(() => {
    if (!isEmpty(get(data, "lateInfoList"))) {
      setLateInfoList(get(data, "lateInfoList"));
    } else {
      setLateInfoList([]);
    }
  }, [data]);

  return (
    <>
      <Typography.Title level={5} className="mt-0">
        {changingBool.value
          ? `Write comment to ${
              get(changingBool, "data.ignore") ? "cancel ignorance" : "ignore"
            }`
          : "User latencies data:"}
      </Typography.Title>

      {changingBool.value ? (
        <ChangeIgnorance
          onChangeFinished={onChangeFinished}
          changingBool={changingBool}
        />
      ) : (
        lateInfoList.map((item: any, i) => (
          <div key={item.id}>
            <Flex
              className="info-header"
              justify="space-between"
              align="center"
            >
              <Typography.Text
                delete={item.ignore}
                type={item.ignore ? "secondary" : undefined}
              >
                {i + 1}. {lateTypes[item.type]}
              </Typography.Text>

              {get(data, "user.allowedIgnoreCount") > 0 &&
              !get(data, "closed") ? (
                <Button
                  onClick={() =>
                    changingBool.onTrue({ ignore: item.ignore, id: item.id })
                  }
                  color={!item.ignore ? "danger" : "primary"}
                  variant="solid"
                >
                  {item.ignore ? "Cancel ignorance" : "Ignore"}
                </Button>
              ) : null}
            </Flex>

            {!isEmpty(item.history) ? (
              <div className="history-list">
                {item.history.map((history: any) => (
                  <div key={history.id} className="history-item">
                    <Flex gap={5} align="start">
                      <Avatar
                        style={{ minWidth: "24px" }}
                        icon={<UserOutlined />}
                        size="small"
                        src={get(usersMap, `${history.bxUserId}.photo`)}
                      />
                      <div style={{ flex: 1 }}>
                        <Typography.Text className="block leading-3">
                          {get(usersMap, `${history.bxUserId}.name`)}{" "}
                          {get(usersMap, `${history.bxUserId}.lastName`)}
                        </Typography.Text>
                        <Typography.Text
                          className="text-xs mt-0"
                          type="secondary"
                        >
                          {moment(history.createdAt).format("DD.MM.yyyy HH:mm")}
                        </Typography.Text>
                      </div>
                      {history.before === "false" ? (
                        <Tag bordered={false} color="error">
                          Ignored
                        </Tag>
                      ) : (
                        <Tag bordered={false} color="success">
                          Ignore canceled
                        </Tag>
                      )}
                    </Flex>

                    <Typography.Paragraph
                      type="secondary"
                      className="mb-0 text-sm"
                    >
                      {history.description}
                    </Typography.Paragraph>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ))
      )}
    </>
  );
};

const UserInOutInfoTab = ({ data, isAdmin }: Props) => {
  const [searchParams] = useSearchParams();

  const openUserPhoto = useBoolean();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const { data: userInOutData, isFetching } = useQuery({
    queryKey: ["user-in-out-data", get(data, "userWorkDayId")],
    queryFn: async () => {
      axiosInstance.defaults.headers.common.Authorization =
        searchParams.get("token");
      return await axiosInstance.get(
        `/turniket/v1/user-work-day/in-out-info/${get(data, "userWorkDayId")}`
      );
    },
  });

  const [userInOutList, setUserInOutList] = useState([]);

  useEffect(() => {
    setUserInOutList(get(userInOutData, "data.data", []));
  }, [userInOutData]);

  const deleteInOut = useMutation({
    mutationFn: async (id: number) => {
      axiosInstance.defaults.headers.common.Authorization =
        searchParams.get("token");
      return await axiosInstance.delete(
        `/turniket/v1/user-work-day/in-out/${id}`
      );
    },
    onSuccess: (_, id) => {
      setUserInOutList((prev) => prev.filter((entry: any) => entry.id !== id));
    },
  });

  return (
    <>
      {isFetching ? (
        <Skeleton />
      ) : (
        <div className="history-list !pl-0" style={{ zIndex: 1 }}>
          {userInOutList?.map((item: any, i: number) => (
            <div key={i} className="history-item !py-2 hover:bg-gray-50 group ">
              <Flex justify="space-between" align="center" gap="8px">
                <Flex gap={10}>
                  {item.status === "ENTER" ? (
                    <TbDoorEnter size="18px" className="text-green-600" />
                  ) : (
                    <TbDoorExit size="18px" className="text-red-600" />
                  )}
                  <Tag
                    bordered={false}
                    color={item.status === "ENTER" ? "success" : "error"}
                  >
                    {moment(item.timestamp).format("DD.MM.YYYY HH:mm")}
                  </Tag>
                </Flex>
                <Flex align="center" gap={10}>
                  {isAdmin && (
                    <Flex className="p-1 bg-red-500 hover:bg-red-600 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Popconfirm
                        okButtonProps={{ loading: isFetching }}
                        onConfirm={() => deleteInOut.mutate(item.id)}
                        placement="leftTop"
                        title={"Are you sure to delete action?"}
                        okText="Yes"
                        okType={"danger"}
                        cancelText="No"
                      >
                        <MdDelete
                          className="text-white cursor-pointer transition-colors duration-200"
                          size="16px"
                        />
                      </Popconfirm>
                    </Flex>
                  )}

                  {item.hasImage && (
                    <div
                      onClick={() =>
                        openUserPhoto.onTrue(
                          `${BASE_URL}turniket/v1/user-work-day/download-img/${
                            item.id
                          }?AUTH_ID=${searchParams.get("token")}`
                        )
                      }
                      style={{
                        backgroundImage: `url("${BASE_URL}turniket/v1/user-work-day/download-img/${
                          item.id
                        }?AUTH_ID=${searchParams.get("token")}")`,
                      }}
                      className="group w-9 h-9 overflow-hidden bg-red-200 bg-cover bg-no-repeat bg-center cursor-pointer rounded-md border-2 border-red-500"
                    >
                      <Flex
                        justify="center"
                        align="center"
                        className="w-full h-full text-transparent hover:bg-green-500 hover:text-white transition-all duration-300"
                      >
                        <FaExpandAlt className="transition-transform duration-500 group-hover:scale-125 -rotate-90 group-hover:rotate-0" />
                      </Flex>
                    </div>
                  )}
                </Flex>
              </Flex>
            </div>
          ))}
        </div>
      )}

      <Image
        width={200}
        style={{ display: "none" }}
        src={openUserPhoto.data}
        preview={{
          visible: openUserPhoto.value,
          scaleStep: 0.5,
          src: openUserPhoto.data,
          onVisibleChange: openUserPhoto.onToggle,
        }}
      />
    </>
  );
};

const LeaveRequestsTab = ({ data }: Props) => {
  return (
    <>
      <Typography.Title level={5} className="mt-0">
        Leaving permissions:
      </Typography.Title>

      <div className="history-list !pl-0">
        {get(data, "temporaryBreaks", [])?.map((item: any, i: number) => (
          <div className="history-item !py-2">
            <Flex justify="space-between" align="center" gap="8px">
              <Typography.Text className="flex-1 font-bold">
                For: {permissionTypes[item.type]}
              </Typography.Text>
              From:{" "}
              <Tag bordered={false} color="success">
                {moment(item.fromDateTime).format("HH:mm")}
              </Tag>
              To:{" "}
              <Tag bordered={false} color="success">
                {moment(item.toDateTime).format("HH:mm")}
              </Tag>
            </Flex>
            <p className="mb-0">{item.text}</p>
          </div>
        ))}
      </div>
    </>
  );
};

type FieldType = {
  comment?: string;
};

const ChangeIgnorance = ({
  changingBool,
  onChangeFinished,
}: {
  changingBool: BooleanReturnType;
  onChangeFinished: (data: any) => void;
}) => {
  const [searchParams] = useSearchParams();

  const { mutate: updateIgnorance, isPending } = useMutation({
    mutationKey: ["/turniket/v1/user-work-day/change-late-info"],
    mutationFn: async (data: any) => {
      axiosInstance.defaults.headers.common.Authorization =
        searchParams.get("token");
      return await axiosInstance.put(
        "/turniket/v1/user-work-day/change-late-info",
        data
      );
    },
    onSuccess: ({ data }) => {
      onChangeFinished(data?.data);
    },
  });

  const onFinish: FormProps<FieldType>["onFinish"] = (values) => {
    updateIgnorance({
      lateInfoId: get(changingBool, "data.id"),
      ignore: !get(changingBool, "data.ignore"),
      description: values.comment,
    });
  };

  return (
    <Form onFinish={onFinish}>
      <Form.Item<FieldType>
        name="comment"
        rules={[{ required: true, message: "Please write comment!" }]}
      >
        <Input.TextArea
          autoFocus
          autoSize={{ minRows: 3 }}
          placeholder="Comment..."
        />
      </Form.Item>
      <Space style={{ justifyContent: "flex-end", width: "100%" }}>
        <Button onClick={changingBool.onFalse} type="dashed">
          Cancel
        </Button>
        <Button loading={isPending} type="primary" htmlType="submit">
          Save
        </Button>
      </Space>
    </Form>
  );
};

export default memo(LateInfoDropdownBody);
